import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ArticleIcon from '@mui/icons-material/Article';
import colors from '../../../styles/colors';
import ResultTabs from './ResultTabs';
import { 
  SCAFFOLD_TOOLS, 
  RESOURCE_TOOLS,
  executeScaffoldTool,
  executeResourceTool
} from './tools';

/**
 * ToolPanel - 工具面板組件（模組化架構）
 * 
 * 右側工具欄，分為上下兩區：
 * - 上區：鷹架工具（需要選擇節點）
 * - 下區：資源工具（獨立使用）
 * 
 * 工具模組化設計：
 * - 工具定義：tools/toolDefinitions.js
 * - 鷹架工具執行器：tools/scaffoldTools.js
 * - 資源工具執行器：tools/resourceTools.js
 * - 統一入口：tools/index.js
 * 
 * @param {Set} selectedNodes - 已選中的節點 ID 集合
 * @param {Array} graphNodes - 所有圖形節點
 * @param {Function} getNodeContent - 獲取節點內容的函數
 * @param {Function} onToolExecute - 工具執行回調（可選，用於自定義處理）
 */

export const ToolPanel = ({
  selectedNodes = new Set(),
  graphNodes = [],
  getNodeContent,
  onToolExecute,
  onWidthChange,
  onScaffoldCreateNode,
  initialWidth = 400,
}) => {
  const [results, setResults] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // 輸入對話框狀態
  const [inputDialogOpen, setInputDialogOpen] = useState(false);
  const [currentTool, setCurrentTool] = useState(null);
  const [inputValue, setInputValue] = useState('');

  // 收合狀態
  const [isToolsPanelExpanded, setIsToolsPanelExpanded] = useState(true);
  const [isResultsPanelExpanded, setIsResultsPanelExpanded] = useState(true);

  // 可調整大小的狀態
  const [panelWidth, setPanelWidth] = useState(initialWidth || 400); // 預設寬度 400px
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  // 上下調整高度的狀態
  const [toolsHeight, setToolsHeight] = useState(300); // 工具區高度
  const [isResizingVertical, setIsResizingVertical] = useState(false);
  const verticalResizeRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  const selectedCount = selectedNodes.size;

  // 委派點擊：鷹架「新增為節點」按鈕 → 收集表單內容並通知父層創建節點（帶特殊標籤）
  useEffect(() => {
    if (!onScaffoldCreateNode) return;
    const handleClick = (e) => {
      const btn = e.target.closest?.('.scaffold-create-node-btn');
      if (!btn) return;
      e.preventDefault();
      const container = btn.closest?.('[data-storage-key]');
      if (!container) return;
      const storageKey = container.getAttribute('data-storage-key');
      const toolId = btn.getAttribute('data-tool-id') || '';
      const scaffoldTag = btn.getAttribute('data-scaffold-tag') || 'idea';
      const defaultTitle = btn.getAttribute('data-default-title') || '新節點';
      const key = storageKey;
      const textareas = Array.from(container.querySelectorAll('textarea')).filter(
        (t) => t.id && t.id.startsWith(key)
      );
      const parts = textareas
        .map((t) => (t.value && t.value.trim()) || '')
        .filter(Boolean);
      const title = parts.length
        ? parts[0].substring(0, 80) + (parts[0].length > 80 ? '...' : '')
        : defaultTitle;
      const content = parts.join('\n\n');
      onScaffoldCreateNode({
        toolId,
        scaffoldTag,
        title,
        content,
      });
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [onScaffoldCreateNode]);

  // 監聽螢幕尺寸變化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * 處理水平拖動開始
   */
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  /**
   * 處理垂直拖動開始
   */
  const handleVerticalMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizingVertical(true);
  }, []);

  /**
   * 處理垂直觸控開始
   */
  const handleVerticalTouchStart = useCallback((e) => {
    e.preventDefault();
    setIsResizingVertical(true);
  }, []);

  /**
   * 處理拖動中
   */
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = window.innerWidth - e.clientX;
      // 限制最小和最大寬度
      const minWidth = 300;
      const maxWidth = window.innerWidth * 0.7; // 最大70%視窗寬度
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setPanelWidth(newWidth);
        if (onWidthChange) {
          onWidthChange(newWidth);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // 防止拖動時選中文字
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing]);

  /**
   * 處理垂直拖動
   */
  useEffect(() => {
    const handleMove = (clientY) => {
      if (!isResizingVertical) return;
      
      const container = verticalResizeRef.current?.parentElement;
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const newHeight = clientY - containerRect.top;
      
      // 限制最小和最大高度
      const minHeight = 150;
      const maxHeight = containerRect.height - 150;
      
      if (newHeight >= minHeight && newHeight <= maxHeight) {
        setToolsHeight(newHeight);
      }
    };

    const handleMouseMove = (e) => handleMove(e.clientY);
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        e.preventDefault();
        handleMove(e.touches[0].clientY);
      }
    };

    const handleEnd = () => {
      setIsResizingVertical(false);
    };

    if (isResizingVertical) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'row-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizingVertical]);

  /**
   * 獲取選中節點的內容
   */
  const getSelectedNodesContent = useCallback(() => {
    const contents = [];
    selectedNodes.forEach((nodeId) => {
      const nodeIdStr = nodeId?.toString();
      const node = graphNodes.find(n => {
        const nId = n.id?.toString();
        const sourceId = n.data?.source?.id?.toString();
        return nId === nodeIdStr || sourceId === nodeIdStr;
      });
      
      if (node) {
        const source = node.data?.source || node;
        contents.push({
          id: nodeIdStr,
          title: source.title || source.label || '',
          content: source.content || source.label || '',
          author: source.author || '',
        });
      } else {
        console.warn(`找不到節點 ID: ${nodeIdStr}`, {
          selectedNodeId: nodeIdStr,
          availableNodeIds: graphNodes.map(n => ({
            id: n.id?.toString(),
            sourceId: n.data?.source?.id?.toString(),
            label: n.data?.label
          }))
        });
      }
    });
    console.log(`選中 ${selectedNodes.size} 個節點，成功獲取 ${contents.length} 個內容`, contents);
    return contents;
  }, [selectedNodes, graphNodes]);

  /**
   * 執行需要節點的工具（鷹架工具）
   */
  const executeNodeTool = useCallback(async (tool) => {
    if (selectedCount < tool.minNodes) {
      return;
    }

    const nodeContents = getSelectedNodesContent();
    
    const newResultId = `result-${Date.now()}`;
    const newResult = {
      id: newResultId,
      title: `${tool.name} (${selectedCount}個節點)`,
      content: '',
      tool: tool.name,
      timestamp: Date.now(),
      loading: true,
    };

    setResults(prev => [...prev, newResult]);
    setActiveTab(results.length);
    setLoading(true);

    try {
      let result;
      
      if (onToolExecute) {
        result = await onToolExecute(tool.id, nodeContents);
      } else {
        // 使用模組化的鷹架工具執行器
        result = executeScaffoldTool(tool.id, nodeContents);
        await new Promise(resolve => setTimeout(resolve, 800));
      }
      
      setResults(prev => 
        prev.map(r => r.id === newResultId 
          ? { ...r, content: result, loading: false }
          : r
        )
      );
    } catch (error) {
      console.error('工具執行失敗:', error);
      setResults(prev => 
        prev.map(r => r.id === newResultId 
          ? { ...r, content: `<p style="color: red;">處理失敗：${error.message}</p>`, loading: false }
          : r
        )
      );
    } finally {
      setLoading(false);
    }
  }, [selectedCount, getSelectedNodesContent, results.length, onToolExecute]);

  /**
   * 處理工具點擊
   */
  const handleToolClick = useCallback((tool) => {
    console.log('工具被點擊:', tool);
    if (tool.requiresInput) {
      // 需要用戶輸入的工具，打開對話框
      setCurrentTool(tool);
      setInputValue('');
      setInputDialogOpen(true);
    } else {
      // 不需要輸入的工具，直接執行
      console.log('執行節點工具:', tool.name);
      executeNodeTool(tool);
    }
  }, [executeNodeTool]);

  /**
   * 執行獨立工具（不需要節點）
   */
  const executeStandaloneTool = useCallback(async (tool, userInput) => {
    const newResultId = `result-${Date.now()}`;
    const newResult = {
      id: newResultId,
      title: `${tool.name}: ${userInput.substring(0, 20)}${userInput.length > 20 ? '...' : ''}`,
      content: '',
      tool: tool.name,
      timestamp: Date.now(),
      loading: true,
    };

    setResults(prev => [...prev, newResult]);
    setActiveTab(results.length);
    setLoading(true);

    try {
      let result;
      
      if (onToolExecute) {
        result = await onToolExecute(tool.id, { query: userInput });
      } else {
        // 預設處理
        result = await generateStandaloneResult(tool, userInput);
      }
      
      setResults(prev => 
        prev.map(r => r.id === newResultId 
          ? { ...r, content: result, loading: false }
          : r
        )
      );
    } catch (error) {
      console.error('工具執行失敗:', error);
      setResults(prev => 
        prev.map(r => r.id === newResultId 
          ? { ...r, content: `<p style="color: red;">處理失敗：${error.message}</p>`, loading: false }
          : r
        )
      );
    } finally {
      setLoading(false);
    }
  }, [results.length, onToolExecute]);

  /**
   * 處理輸入對話框確認
   */
  const handleInputConfirm = useCallback(() => {
    if (!inputValue.trim() || !currentTool) return;
    
    setInputDialogOpen(false);
    executeStandaloneTool(currentTool, inputValue.trim());
    setInputValue('');
    setCurrentTool(null);
  }, [inputValue, currentTool, executeStandaloneTool]);

  /**
   * 處理輸入對話框取消
   */
  const handleInputCancel = useCallback(() => {
    setInputDialogOpen(false);
    setInputValue('');
    setCurrentTool(null);
  }, []);

  /**
   * 生成預設結果（需要節點的工具）- 鷹架引導表單
   */
  const generateDefaultResult = (tool, nodeContents) => {
    const nodeList = nodeContents.map((n, i) => 
      `<li style="margin-bottom: 8px;"><strong style="color: #1976d2;">${n.title || `節點 ${i + 1}`}</strong><br/><span style="color: #666; font-size: 0.9rem;">${n.content?.substring(0, 150) || '(無內容)'}${n.content?.length > 150 ? '...' : ''}</span><br/><em style="color: #999; font-size: 0.85rem;">— ${n.author || '未知作者'}</em></li>`
    ).join('');

    switch (tool.id) {
      case 'summarize':
        return `
          <div style="padding: 16px; font-family: system-ui, -apple-system, sans-serif;">
            <h4 style="color: #1976d2; margin-bottom: 16px;">📝 摘要鷹架</h4>
            
            <div style="background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
              <h5 style="color: #424242; margin: 0 0 8px 0; font-size: 0.9rem;">已選擇的節點：</h5>
              <ul style="list-style: none; padding: 0; margin: 0;">${nodeList}</ul>
            </div>

            <div style="background: #e3f2fd; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
              <h5 style="color: #1565c0; margin: 0 0 12px 0;">💭 引導提問</h5>
              
              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  1. 這${nodeContents.length}個想法的核心主題是什麼？
                </label>
                <textarea 
                  style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #bbdefb; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="在此輸入你的想法..."></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  2. 如何用一句話總結這些觀點？
                </label>
                <textarea 
                  style="width: 100%; min-height: 60px; padding: 10px; border: 2px solid #bbdefb; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="試著濃縮成一句話..."></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  3. 這些內容的關鍵要點有哪些？
                </label>
                <textarea 
                  style="width: 100%; min-height: 100px; padding: 10px; border: 2px solid #bbdefb; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="列出 3-5 個關鍵要點..."></textarea>
              </div>
            </div>

            <div style="background: #fff3e0; padding: 12px; border-radius: 8px; border-left: 4px solid #ff9800;">
              <p style="margin: 0; font-size: 0.9rem; color: #e65100;"><strong>💡 建議步驟：</strong></p>
              <ol style="margin: 8px 0 0 0; padding-left: 20px; font-size: 0.9rem; color: #424242;">
                <li>找出共同點和差異</li>
                <li>提煉關鍵概念</li>
                <li>組織成連貫的摘要</li>
              </ol>
            </div>
          </div>
        `;
      
      case 'compare':
        return `
          <div style="padding: 16px; font-family: system-ui, -apple-system, sans-serif;">
            <h4 style="color: #1976d2; margin-bottom: 16px;">⚖️ 比較鷹架</h4>
            
            <div style="background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
              <h5 style="color: #424242; margin: 0 0 8px 0; font-size: 0.9rem;">要比較的節點：</h5>
              <ul style="list-style: none; padding: 0; margin: 0;">${nodeList}</ul>
            </div>

            <div style="background: #f3e5f5; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
              <h5 style="color: #6a1b9a; margin: 0 0 12px 0;">💭 比較維度</h5>
              
              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  相似之處
                </label>
                <textarea 
                  style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #e1bee7; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="這些想法在哪些方面相似？共同強調什麼？"></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  差異之處
                </label>
                <textarea 
                  style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #e1bee7; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="在哪些方面不同？立場或角度有何差異？"></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  觀點立場分析
                </label>
                <textarea 
                  style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #e1bee7; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="哪些觀點相互支持？哪些存在矛盾或衝突？"></textarea>
              </div>
            </div>

            <div style="background: #e8f5e9; padding: 12px; border-radius: 8px; border-left: 4px solid #4caf50;">
              <p style="margin: 0; font-size: 0.9rem; color: #2e7d32;"><strong>📊 其他可比較的維度：</strong></p>
              <ul style="margin: 8px 0 0 0; padding-left: 20px; font-size: 0.85rem; color: #424242;">
                <li>論證方式：理論 vs. 實例 vs. 數據</li>
                <li>深度廣度：具體細節 vs. 宏觀視角</li>
                <li>創新程度：傳統想法 vs. 新穎觀點</li>
              </ul>
            </div>
          </div>
        `;
      
      case 'question':
        return `
          <div style="padding: 16px; font-family: system-ui, -apple-system, sans-serif;">
            <h4 style="color: #1976d2; margin-bottom: 16px;">❓ 提問鷹架</h4>
            
            <div style="background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
              <h5 style="color: #424242; margin: 0 0 8px 0; font-size: 0.9rem;">基於以下節點發展問題：</h5>
              <ul style="list-style: none; padding: 0; margin: 0;">${nodeList}</ul>
            </div>

            <div style="background: #e8f5e9; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
              <h5 style="color: #2e7d32; margin: 0 0 12px 0;">💭 發展你的問題</h5>
              
              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  澄清型問題 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（釐清意義和定義）</span>
                </label>
                <textarea 
                  style="width: 100%; min-height: 70px; padding: 10px; border: 2px solid #c8e6c9; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="例如：「這是什麼意思？」「為什麼會這樣？」"></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  挑戰型問題 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（質疑和批判）</span>
                </label>
                <textarea 
                  style="width: 100%; min-height: 70px; padding: 10px; border: 2px solid #c8e6c9; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="例如：「這個觀點的限制是什麼？」「有沒有反例？」"></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  延伸型問題 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（拓展思考）</span>
                </label>
                <textarea 
                  style="width: 100%; min-height: 70px; padding: 10px; border: 2px solid #c8e6c9; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="例如：「如果...會怎樣？」「還可以探索什麼？」"></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  連結型問題 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（建立關聯）</span>
                </label>
                <textarea 
                  style="width: 100%; min-height: 70px; padding: 10px; border: 2px solid #c8e6c9; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="例如：「這與...有什麼關係？」「如何連結其他概念？」"></textarea>
              </div>
            </div>

            <div style="background: #fff9c4; padding: 12px; border-radius: 8px; border-left: 4px solid #fbc02d;">
              <p style="margin: 0; font-size: 0.85rem; color: #f57f17;">
                💡 <strong>提示：</strong>好問題能引導更深入的思考，試著從不同角度提問！
              </p>
            </div>
          </div>
        `;
      
      case 'insight':
        return `
          <div style="padding: 16px; font-family: system-ui, -apple-system, sans-serif;">
            <h4 style="color: #1976d2; margin-bottom: 16px;">💡 洞察鷹架</h4>
            
            <div style="background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
              <h5 style="color: #424242; margin: 0 0 8px 0; font-size: 0.9rem;">分析以下節點：</h5>
              <ul style="list-style: none; padding: 0; margin: 0;">${nodeList}</ul>
            </div>

            <div style="background: #fff9c4; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
              <h5 style="color: #f57f17; margin: 0 0 12px 0;">💭 深度思考</h5>
              
              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  隱含的深層道理
                </label>
                <textarea 
                  style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #fff59d; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="這些想法背後隱含了什麼更深層的道理或原理？"></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  模式與趨勢
                </label>
                <textarea 
                  style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #fff59d; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="你看到了什麼模式、規律或趨勢？"></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  意外的發現
                </label>
                <textarea 
                  style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #fff59d; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="有什麼是作者沒有明說，但你能察覺到的？有什麼出乎意料的連結？"></textarea>
              </div>
            </div>

            <div style="background: #e1f5fe; padding: 12px; border-radius: 8px; border-left: 4px solid #0288d1;">
              <p style="margin: 0 0 8px 0; font-size: 0.9rem; color: #01579b;"><strong>🔍 洞察提示：</strong></p>
              <ul style="margin: 0; padding-left: 20px; font-size: 0.85rem; color: #424242;">
                <li>這些觀點基於什麼假設或前提？</li>
                <li>反映了什麼價值觀或立場？</li>
                <li>各部分如何相互影響或連結？</li>
              </ul>
            </div>
          </div>
        `;
      
      case 'expand':
        return `
          <div style="padding: 16px; font-family: system-ui, -apple-system, sans-serif;">
            <h4 style="color: #1976d2; margin-bottom: 16px;">🚀 延伸鷹架</h4>
            
            <div style="background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
              <h5 style="color: #424242; margin: 0 0 8px 0; font-size: 0.9rem;">延伸以下節點：</h5>
              <ul style="list-style: none; padding: 0; margin: 0;">${nodeList}</ul>
            </div>

            <div style="background: #fce4ec; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
              <h5 style="color: #c2185b; margin: 0 0 12px 0;">💭 延伸方向</h5>
              
              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  廣度延伸 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（相關領域、跨學科連結）</span>
                </label>
                <textarea 
                  style="width: 100%; min-height: 75px; padding: 10px; border: 2px solid #f8bbd0; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="這些想法可以連結到哪些其他領域或議題？"></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  深度延伸 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（細節分析、機制探討）</span>
                </label>
                <textarea 
                  style="width: 100%; min-height: 75px; padding: 10px; border: 2px solid #f8bbd0; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="可以進一步深入探討哪些細節或運作機制？"></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  實踐延伸 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（應用場景、解決方案）</span>
                </label>
                <textarea 
                  style="width: 100%; min-height: 75px; padding: 10px; border: 2px solid #f8bbd0; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="如何將這些想法應用到實際情境？可以解決什麼問題？"></textarea>
              </div>

              <div style="margin-bottom: 16px;">
                <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
                  批判延伸 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（反思限制、提出改進）</span>
                </label>
                <textarea 
                  style="width: 100%; min-height: 75px; padding: 10px; border: 2px solid #f8bbd0; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
                  placeholder="這些想法有什麼限制？如何改進或補充？"></textarea>
              </div>
            </div>

            <div style="background: #e0f2f1; padding: 12px; border-radius: 8px; border-left: 4px solid #00897b;">
              <p style="margin: 0; font-size: 0.85rem; color: #004d40;">
                💡 <strong>提示：</strong>延伸思考幫助你從現有想法出發，探索更多可能性！
              </p>
            </div>
          </div>
        `;
      
      default:
        return `<p>工具 ${tool.name} 的結果</p>`;
    }
  };

  /**
   * 生成獨立工具的結果
   */
  const generateStandaloneResult = async (tool, userInput) => {
    switch (tool.id) {
      case 'google-search': {
        const encodedQuery = encodeURIComponent(userInput);
        // 使用 Google 嵌入式搜尋 URL
        const googleEmbedUrl = `https://www.google.com/search?igu=1&q=${encodedQuery}`;
        // 備用：DuckDuckGo（更友好的 iframe 支援）
        const duckduckgoUrl = `https://duckduckgo.com/?q=${encodedQuery}&ia=web`;
        
        return `
          <div style="display: flex; flex-direction: column; height: 100%; min-height: 400px;">
            <div style="padding: 8px 12px; background: #f8f9fa; border-bottom: 1px solid #e0e0e0; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;">
              <div>
                <span style="font-weight: 600; color: #1a73e8;">🔍 搜尋：</span>
                <span style="color: #333;">${userInput}</span>
              </div>
              <a 
                href="https://www.google.com/search?q=${encodedQuery}" 
                target="_blank" 
                rel="noopener noreferrer"
                style="font-size: 0.8rem; color: #1a73e8; text-decoration: none;"
              >
                在新分頁開啟 ↗
              </a>
            </div>
            <iframe 
              src="${googleEmbedUrl}"
              style="flex: 1; width: 100%; border: none; min-height: 350px;"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              loading="lazy"
              title="Google 搜尋結果"
              onerror="this.style.display='none'; this.nextElementSibling.style.display='block';"
            ></iframe>
            <div style="display: none; padding: 20px; text-align: center; background: #fff3cd; border-radius: 8px; margin: 10px;">
              <p style="margin: 0 0 12px 0; color: #856404;">⚠️ Google 搜尋無法在此嵌入顯示</p>
              <p style="margin: 0 0 16px 0; font-size: 0.9rem; color: #666;">
                由於安全限制，請使用以下替代方式：
              </p>
              <div style="display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;">
                <a 
                  href="https://www.google.com/search?q=${encodedQuery}" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style="padding: 8px 16px; background: #1a73e8; color: white; text-decoration: none; border-radius: 6px; font-size: 0.9rem;"
                >
                  開啟 Google 搜尋
                </a>
                <a 
                  href="${duckduckgoUrl}" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style="padding: 8px 16px; background: #de5833; color: white; text-decoration: none; border-radius: 6px; font-size: 0.9rem;"
                >
                  使用 DuckDuckGo
                </a>
              </div>
            </div>
          </div>
        `;
      }
      case 'openalex': {
        const encodedQuery = encodeURIComponent(userInput);
        const apiUrl = `https://api.openalex.org/works?search=${encodedQuery}&per-page=20`;
        
        try {
          const response = await fetch(apiUrl);
          const data = await response.json();
          
          if (!data.results || data.results.length === 0) {
            return `
              <div style="padding: 20px; text-align: center;">
                <p style="color: #666;">沒有找到相關論文</p>
                <p style="font-size: 0.85rem; color: #999;">請嘗試其他關鍵字</p>
              </div>
            `;
          }
          
          const papers = data.results.map((paper, index) => `
            <div style="padding: 12px; border-bottom: 1px solid #eee; background: ${index % 2 === 0 ? '#fafafa' : '#fff'};">
              <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 6px;">
                <h4 style="margin: 0; font-size: 0.95rem; color: #2e7d32; flex: 1;">
                  ${paper.doi ? `<a href="https://doi.org/${paper.doi}" target="_blank" rel="noopener noreferrer" style="color: #2e7d32; text-decoration: none;">${paper.display_name || '無標題'}</a>` : paper.display_name || '無標題'}
                </h4>
                <span style="background: #e8f5e9; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; color: #2e7d32; white-space: nowrap; margin-left: 8px;">
                  ${paper.publication_year || 'N/A'}
                </span>
              </div>
              <p style="margin: 4px 0; font-size: 0.8rem; color: #666;">
                <strong>作者：</strong>${paper.authorships?.slice(0, 3).map(a => a.author?.display_name).filter(Boolean).join(', ') || '未知'}${paper.authorships?.length > 3 ? ' 等' : ''}
              </p>
              ${paper.primary_location?.source?.display_name ? `<p style="margin: 4px 0; font-size: 0.8rem; color: #666;"><strong>期刊：</strong>${paper.primary_location.source.display_name}</p>` : ''}
              ${paper.abstract_inverted_index ? `<p style="margin: 6px 0 0 0; font-size: 0.85rem; color: #444; line-height: 1.4;">摘要可用</p>` : ''}
              <div style="margin-top: 6px; display: flex; gap: 12px; font-size: 0.75rem; color: #999;">
                <span>📊 引用：${paper.cited_by_count || 0}</span>
                ${paper.type ? `<span>📝 ${paper.type.replace('https://openalex.org/', '')}</span>` : ''}
                ${paper.open_access?.is_oa ? '<span style="color: #2e7d32;">✅ 開放存取</span>' : ''}
              </div>
            </div>
          `).join('');
          
          return `
            <div style="height: 100%; display: flex; flex-direction: column;">
              <div style="padding: 8px 12px; background: #f1f8f4; border-bottom: 1px solid #c8e6c9; flex-shrink: 0;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <span style="font-weight: 600; color: #2e7d32;">📚 OpenAlex 搜尋：${userInput}</span>
                  <span style="font-size: 0.75rem; color: #666;">找到 ${data.results.length} 篇論文</span>
                </div>
              </div>
              <div style="flex: 1; overflow-y: auto;">
                ${papers}
              </div>
            </div>
          `;
        } catch (error) {
          return `
            <div style="padding: 20px; text-align: center;">
              <p style="color: #d32f2f;">❗ API 請求失敗</p>
              <p style="font-size: 0.85rem; color: #666;">${error.message}</p>
              <a href="https://openalex.org/" target="_blank" rel="noopener noreferrer" style="color: #2e7d32;">
                訪問 OpenAlex →
              </a>
            </div>
          `;
        }
      }
      case 'crossref': {
        const encodedQuery = encodeURIComponent(userInput);
        const apiUrl = `https://api.crossref.org/works?query=${encodedQuery}&rows=20&filter=type:journal-article`;
        
        try {
          const response = await fetch(apiUrl);
          const data = await response.json();
          
          if (!data.message?.items || data.message.items.length === 0) {
            return `
              <div style="padding: 20px; text-align: center;">
                <p style="color: #666;">沒有找到相關論文</p>
                <p style="font-size: 0.85rem; color: #999;">請嘗試其他關鍵字</p>
              </div>
            `;
          }
          
          const papers = data.message.items.map((paper, index) => `
            <div style="padding: 12px; border-bottom: 1px solid #eee; background: ${index % 2 === 0 ? '#fafafa' : '#fff'};">
              <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 6px;">
                <h4 style="margin: 0; font-size: 0.95rem; color: #e65100; flex: 1;">
                  ${paper.DOI ? `<a href="https://doi.org/${paper.DOI}" target="_blank" rel="noopener noreferrer" style="color: #e65100; text-decoration: none;">${paper.title?.[0] || '無標題'}</a>` : paper.title?.[0] || '無標題'}
                </h4>
                <span style="background: #fff3e0; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; color: #e65100; white-space: nowrap; margin-left: 8px;">
                  ${paper.published?.['date-parts']?.[0]?.[0] || paper.created?.['date-parts']?.[0]?.[0] || 'N/A'}
                </span>
              </div>
              <p style="margin: 4px 0; font-size: 0.8rem; color: #666;">
                <strong>作者：</strong>${paper.author?.slice(0, 3).map(a => `${a.given || ''} ${a.family || ''}`).join(', ') || '未知'}${paper.author?.length > 3 ? ' 等' : ''}
              </p>
              ${paper['container-title']?.[0] ? `<p style="margin: 4px 0; font-size: 0.8rem; color: #666;"><strong>期刊：</strong>${paper['container-title'][0]}</p>` : ''}
              ${paper.abstract ? `<p style="margin: 6px 0 0 0; font-size: 0.85rem; color: #444; line-height: 1.4;">${paper.abstract.substring(0, 200)}${paper.abstract.length > 200 ? '...' : ''}</p>` : ''}
              <div style="margin-top: 6px; display: flex; gap: 12px; font-size: 0.75rem; color: #999;">
                ${paper.DOI ? `<span>🆔 DOI: ${paper.DOI}</span>` : ''}
                ${paper['is-referenced-by-count'] ? `<span>📊 引用：${paper['is-referenced-by-count']}</span>` : ''}
                ${paper.type ? `<span>📝 ${paper.type}</span>` : ''}
              </div>
            </div>
          `).join('');
          
          return `
            <div style="height: 100%; display: flex; flex-direction: column;">
              <div style="padding: 8px 12px; background: #fff8e1; border-bottom: 1px solid #ffcc80; flex-shrink: 0;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <span style="font-weight: 600; color: #e65100;">📝 Crossref 搜尋：${userInput}</span>
                  <span style="font-size: 0.75rem; color: #666;">找到 ${data.message.items.length} 篇論文</span>
                </div>
              </div>
              <div style="flex: 1; overflow-y: auto;">
                ${papers}
              </div>
            </div>
          `;
        } catch (error) {
          return `
            <div style="padding: 20px; text-align: center;">
              <p style="color: #d32f2f;">❗ API 請求失敗</p>
              <p style="font-size: 0.85rem; color: #666;">${error.message}</p>
              <a href="https://api.crossref.org/" target="_blank" rel="noopener noreferrer" style="color: #e65100;">
                訪問 Crossref API →
              </a>
            </div>
          `;
        }
      }
      default:
        return `<p>工具 ${tool.name} 的結果</p>`;
    }
  };

  /**
   * 關閉結果分頁
   */
  const handleCloseTab = useCallback((resultId) => {
    setResults(prev => {
      const newResults = prev.filter(r => r.id !== resultId);
      if (activeTab >= newResults.length && newResults.length > 0) {
        setActiveTab(newResults.length - 1);
      } else if (newResults.length === 0) {
        setActiveTab(0);
      }
      return newResults;
    });
  }, [activeTab]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        position: 'relative',
        width: panelWidth,
        minWidth: '300px',
        maxWidth: '70vw',
      }}
    >
      {/* 拖動手柄 - 僅在桌面顯示 */}
      {!isMobile && (
      <Box
        ref={resizeRef}
        onMouseDown={handleMouseDown}
        sx={{
          width: '8px',
          cursor: 'col-resize',
          backgroundColor: isResizing ? colors.primary.main : 'transparent',
          borderLeft: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: colors.primary.light || '#e3f2fd',
          },
          '&:hover .drag-icon': {
            opacity: 1,
          },
        }}
      >
        <DragIndicatorIcon 
          className="drag-icon"
          sx={{ 
            fontSize: '16px',
            color: '#999',
            opacity: 0,
            transition: 'opacity 0.2s',
            pointerEvents: 'none',
          }} 
        />
      </Box>
      )}

      {/* 主要內容區 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flex: 1,
          backgroundColor: '#fff',
          overflow: 'hidden',
        }}
      >
      {/* 上區：工具列表 */}
      <Box
        sx={{
          height: isMobile ? 'auto' : toolsHeight,
          minHeight: isMobile ? '120px' : '150px',
          maxHeight: isMobile ? '35%' : 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
          flexShrink: 0,
        }}
      >
        <Box sx={{ padding: 2, overflow: 'auto', flex: 1 }}>
        {/* 需要節點的工具 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            鷹架工具
          </Typography>
          <Chip 
            label={`已選 ${selectedCount} 個`}
            size="small"
            color={selectedCount > 0 ? 'primary' : 'default'}
            sx={{ fontSize: '0.75rem' }}
          />
        </Box>

        {selectedCount === 0 && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}>
            選擇節點後可使用以下工具
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {SCAFFOLD_TOOLS.map((tool) => {
            const isDisabled = selectedCount < tool.minNodes || loading;
            return (
              <Tooltip 
                key={tool.id} 
                title={
                  selectedCount < tool.minNodes 
                    ? `需要至少選擇 ${tool.minNodes} 個節點` 
                    : tool.description
                }
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={tool.icon}
                    disabled={isDisabled}
                    onClick={() => handleToolClick(tool)}
                    sx={{
                      textTransform: 'none',
                      borderColor: isDisabled ? '#ddd' : colors.primary.border,
                      color: isDisabled ? '#999' : colors.primary.text,
                      '&:hover': {
                        borderColor: colors.primary.main,
                        backgroundColor: colors.primary.background,
                      },
                    }}
                    aria-label={`${tool.name}工具`}
                  >
                    {tool.name}
                  </Button>
                </span>
              </Tooltip>
            );
          })}
        </Box>

        {/* 獨立工具 */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          資源工具
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {RESOURCE_TOOLS.map((tool) => (
            <Tooltip 
              key={tool.id} 
              title={tool.description}
              arrow
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={loading ? <CircularProgress size={16} /> : tool.icon}
                disabled={loading}
                onClick={() => handleToolClick(tool)}
                sx={{
                  textTransform: 'none',
                  borderColor: colors.secondary.border,
                  color: colors.secondary.text,
                  '&:hover': {
                    borderColor: colors.secondary.main,
                    backgroundColor: colors.secondary.background,
                  },
                }}
                aria-label={`${tool.name}工具`}
              >
                {tool.name}
              </Button>
            </Tooltip>
          ))}
        </Box>
        </Box>
      </Box>

      {/* 垂直拖動手柄 */}
      <Box
        ref={verticalResizeRef}
        onMouseDown={handleVerticalMouseDown}
        onTouchStart={handleVerticalTouchStart}
        sx={{
          height: '8px',
          cursor: 'row-resize',
          backgroundColor: isResizingVertical ? colors.primary.main : 'transparent',
          borderTop: '1px solid #e0e0e0',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: colors.primary.light || '#e3f2fd',
          },
          '&:hover .drag-icon': {
            opacity: 1,
          },
        }}
      >
        <DragIndicatorIcon 
          className="drag-icon"
          sx={{ 
            fontSize: '16px',
            color: '#999',
            opacity: 0,
            transition: 'opacity 0.2s',
            pointerEvents: 'none',
            transform: 'rotate(90deg)',
          }} 
        />
      </Box>

      {/* 下區：結果展示區 */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ 
          padding: '8px 16px', 
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            結果區
          </Typography>
          {results.length > 0 && (
            <Typography variant="caption" sx={{ color: '#999' }}>
              {results.length} 個結果
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <ResultTabs
            results={results}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onCloseTab={handleCloseTab}
          />
        </Box>
      </Box>

      {/* 輸入對話框 */}
      <Dialog 
        open={inputDialogOpen} 
        onClose={handleInputCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {currentTool?.icon && (
            <Box component="span" sx={{ mr: 1, verticalAlign: 'middle' }}>
              {currentTool.icon}
            </Box>
          )}
          {currentTool?.name}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={currentTool?.inputLabel || '請輸入'}
            placeholder={currentTool?.inputPlaceholder || ''}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleInputConfirm();
              }
            }}
            sx={{ mt: 1 }}
            aria-label={currentTool?.inputLabel}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInputCancel} color="inherit">
            取消
          </Button>
          <Button 
            onClick={handleInputConfirm} 
            variant="contained"
            disabled={!inputValue.trim()}
          >
            確認
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

ToolPanel.propTypes = {
  selectedNodes: PropTypes.instanceOf(Set),
  graphNodes: PropTypes.array,
  getNodeContent: PropTypes.func,
  onToolExecute: PropTypes.func,
  onWidthChange: PropTypes.func,
  onScaffoldCreateNode: PropTypes.func,
  initialWidth: PropTypes.number,
};

export default ToolPanel;
